import axios from "axios";
import axiosInstance from "@/app/axiosInstance";
import { API } from "@/app/api";
import {
  BACKEND_UNAVAILABLE_STATUS,
  isBackendUnavailableStatus,
  markApiConnectionHealthy,
  markApiConnectionUnavailable,
} from "@/app/shared/api-connection-health";

interface IResponseLike {
  status?: unknown;
  message?: unknown;
}

interface IPostApiOptions {
  retry?: "auto" | "never" | "always";
  maxRetries?: number;
}

const RETRY_BASE_DELAY_MS = 5_000;
const RETRY_MAX_DELAY_MS = 60_000;
const RETRY_DEFAULT_MAX_RETRIES = 5;

export async function postApi<TResponse>(
  url: string,
  payload?: unknown,
  options?: IPostApiOptions
): Promise<TResponse> {
  const requestBody = payload == undefined
    ? undefined
    : typeof payload == "string"
      ? payload
      : JSON.stringify(payload);
  const retryMode = options?.retry ?? "auto";
  const maxRetries = normalizeMaxRetries(options?.maxRetries);
  let retryAttempt = 0;

  while (true) {
    try {
      const response = requestBody == undefined
        ? await axiosInstance.post(url)
        : await axiosInstance.post(url, requestBody);

      markApiConnectionHealthy();
      return response.data as TResponse;
    } catch (error) {
      const status = normalizeErrorStatus(error);
      if (isBackendUnavailableStatus(status)) {
        markApiConnectionUnavailable(status);
      }

      const shouldRetry = resolveRetryAllowed({
        url,
        status,
        retryMode,
        retryAttempt,
        maxRetries,
      });
      if (!shouldRetry) {
        throw error;
      }

      const delayMs = provideRetryDelayMs(retryAttempt);
      await wait(delayMs);
      retryAttempt += 1;
    }
  }
}

export function getResponseStatus(
  response: unknown,
  fallback = API.Status.Unknown
): string {
  return extractStatus(response) ?? fallback;
}

export function normalizeErrorStatus(
  error: unknown,
  fallback = API.Status.Unknown
): string {
  if (axios.isAxiosError(error)) {
    if (isAxiosBackendUnavailable(error)) {
      return BACKEND_UNAVAILABLE_STATUS;
    }

    const responseStatus = extractStatus(error.response?.data);
    if (responseStatus != undefined) {
      return sanitizeStatus(responseStatus, fallback);
    }

    const errorStatus = extractStatus(error);
    if (errorStatus != undefined) {
      return sanitizeStatus(errorStatus, fallback);
    }

    if (typeof error.code == "string" && error.code.trim().length > 0) {
      return sanitizeStatus(error.code, fallback);
    }
    if (typeof error.message == "string" && error.message.trim().length > 0) {
      return sanitizeStatus(error.message, fallback);
    }
    return fallback;
  }

  const status = extractStatus(error);
  if (status != undefined) return sanitizeStatus(status, fallback);

  if (typeof error == "string" && error.trim().length > 0) {
    return sanitizeStatus(error, fallback);
  }

  if (
    error != undefined &&
    typeof error == "object" &&
    "message" in error
  ) {
    const message = (error as IResponseLike).message;
    if (typeof message == "string" && message.trim().length > 0) {
      return sanitizeStatus(message, fallback);
    }
  }

  return fallback;
}

function extractStatus(value: unknown): string | undefined {
  if (value == undefined) return undefined;

  if (typeof value == "string") {
    const trimmed = value.trim();
    if (trimmed.length == 0) return undefined;
    if (isLikelyHtml(trimmed)) {
      return BACKEND_UNAVAILABLE_STATUS;
    }
    const parsed = tryParseJson(trimmed);
    if (parsed != undefined) {
      const parsedStatus = extractStatus(parsed);
      if (parsedStatus != undefined) return parsedStatus;
    }
    return trimmed;
  }

  if (typeof value != "object" || Array.isArray(value)) return undefined;

  const asResponse = value as IResponseLike;
  if (typeof asResponse.status == "string" && asResponse.status.trim().length > 0) {
    return asResponse.status.trim();
  }
  return undefined;
}

function tryParseJson(value: string): unknown | undefined {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function sanitizeStatus(value: string, fallback: string): string {
  const trimmed = value.trim();
  if (trimmed.length == 0) return fallback;
  if (isBackendUnavailableStatus(trimmed)) return BACKEND_UNAVAILABLE_STATUS;
  if (isLikelyHtml(trimmed)) return BACKEND_UNAVAILABLE_STATUS;
  return trimmed.length > 240 ? trimmed.slice(0, 240) : trimmed;
}

function isLikelyHtml(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  if (normalized.length == 0) return false;
  if (normalized.startsWith("<!doctype html")) return true;
  if (normalized.startsWith("<html")) return true;
  return normalized.includes("<body") && normalized.includes("</html>");
}

function isAxiosBackendUnavailable(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;

  const httpStatus = error.response?.status;
  if (typeof httpStatus == "number" && httpStatus >= 500) {
    return true;
  }

  const responseData = error.response?.data;
  if (typeof responseData == "string" && isLikelyHtml(responseData)) {
    return true;
  }

  if (
    typeof error.code == "string" &&
    isBackendUnavailableStatus(error.code)
  ) {
    return true;
  }

  if (
    typeof error.message == "string" &&
    isBackendUnavailableStatus(error.message)
  ) {
    return true;
  }

  return false;
}

function normalizeMaxRetries(value: number | undefined): number {
  if (typeof value != "number" || !Number.isFinite(value)) {
    return RETRY_DEFAULT_MAX_RETRIES;
  }
  return Math.max(0, Math.floor(value));
}

function provideRetryDelayMs(retryAttempt: number): number {
  const safeAttempt = Math.max(0, Math.min(retryAttempt, 8));
  const delay = RETRY_BASE_DELAY_MS * 2 ** safeAttempt;
  return Math.min(delay, RETRY_MAX_DELAY_MS);
}

function resolveRetryAllowed(params: {
  url: string;
  status: string;
  retryMode: "auto" | "never" | "always";
  retryAttempt: number;
  maxRetries: number;
}): boolean {
  const {
    url,
    status,
    retryMode,
    retryAttempt,
    maxRetries,
  } = params;

  if (!isBackendUnavailableStatus(status)) return false;
  if (retryAttempt >= maxRetries) return false;
  if (retryMode == "never") return false;
  if (retryMode == "always") return true;
  return isRetryableReadEndpoint(url);
}

function isRetryableReadEndpoint(url: string): boolean {
  const normalized = url.toLowerCase();
  if (normalized.includes("/api/backendquery")) return true;
  if (normalized.includes("/api/backendget")) return true;
  if (normalized.includes("/api/backendpoolluck")) return true;
  if (normalized.includes("/api/userget")) return true;
  if (normalized.includes("/api/userenumerate")) return true;
  if (normalized.includes("/api/userquery")) return true;
  if (normalized.includes("/api/instanceenumerateall")) return true;
  return false;
}

function wait(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, timeoutMs);
  });
}
